/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ExportExecutionResultPathParameters,
  GenerateFiltersForExecutionResultPathParameters,
  GetExecutionResultPathParameters,
  ListExecutionsParams,
  TLBulkExportExecutionResultRequest,
  TLBulkExportExecutionResultResponse,
  TLCancelExecutionRequest,
  TLCancelExecutionResponse,
  TLDeleteExecutionRequest,
  TLDeleteExecutionResponse,
  TLExportExecutionResultRequest,
  TLExportExecutionResultResponse,
  TLGenerateFiltersForExecutionRequest,
  TLGenerateFiltersResponse,
  TLGetExecutionResultRequest,
  TLGetExecutionResultResponse,
  TLHTTPValidationError,
  TLListExecutionsResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Executions
 */
export const listExecutions = (
  params?: ListExecutionsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLListExecutionsResponse>(
    { url: `/v2/data-harmonization/executions`, method: 'GET', params, signal },
    options,
  );
};

export const getListExecutionsQueryKey = (params?: ListExecutionsParams) => {
  return ['v2', 'data-harmonization', 'executions', ...(params ? [params] : [])] as const;
};

export const getListExecutionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListExecutionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListExecutionsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listExecutions>>> = ({ signal }) =>
    listExecutions(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listExecutions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListExecutionsQueryResult = NonNullable<Awaited<ReturnType<typeof listExecutions>>>;
export type ListExecutionsQueryError = ErrorType<TLHTTPValidationError>;

export function useListExecutions<
  TData = Awaited<ReturnType<typeof listExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListExecutionsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listExecutions>>,
          TError,
          Awaited<ReturnType<typeof listExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListExecutions<
  TData = Awaited<ReturnType<typeof listExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListExecutionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listExecutions>>,
          TError,
          Awaited<ReturnType<typeof listExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListExecutions<
  TData = Awaited<ReturnType<typeof listExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListExecutionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Executions
 */

export function useListExecutions<
  TData = Awaited<ReturnType<typeof listExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListExecutionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListExecutionsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Executions
 */
export const prefetchListExecutionsQuery = async <
  TData = Awaited<ReturnType<typeof listExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListExecutionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListExecutionsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Executions
 */
export const invalidateListExecutions = async (
  queryClient: QueryClient,
  params?: ListExecutionsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListExecutionsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Cancel Executions
 */
export const cancelExecutions = (
  tLCancelExecutionRequest: BodyType<TLCancelExecutionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCancelExecutionResponse>(
    {
      url: `/v2/data-harmonization/executions/cancel`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCancelExecutionRequest,
      signal,
    },
    options,
  );
};

export const getCancelExecutionsQueryKey = (tLCancelExecutionRequest?: BodyType<TLCancelExecutionRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'executions', 'cancel', tLCancelExecutionRequest] as const;
};

export const getCancelExecutionsQueryOptions = <
  TData = Awaited<ReturnType<typeof cancelExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCancelExecutionRequest: BodyType<TLCancelExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCancelExecutionsQueryKey(tLCancelExecutionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof cancelExecutions>>> = ({ signal }) =>
    cancelExecutions(tLCancelExecutionRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof cancelExecutions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CancelExecutionsQueryResult = NonNullable<Awaited<ReturnType<typeof cancelExecutions>>>;
export type CancelExecutionsQueryError = ErrorType<TLHTTPValidationError>;

export function useCancelExecutions<
  TData = Awaited<ReturnType<typeof cancelExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCancelExecutionRequest: BodyType<TLCancelExecutionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelExecutions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelExecutions>>,
          TError,
          Awaited<ReturnType<typeof cancelExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelExecutions<
  TData = Awaited<ReturnType<typeof cancelExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCancelExecutionRequest: BodyType<TLCancelExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelExecutions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelExecutions>>,
          TError,
          Awaited<ReturnType<typeof cancelExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelExecutions<
  TData = Awaited<ReturnType<typeof cancelExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCancelExecutionRequest: BodyType<TLCancelExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel Executions
 */

export function useCancelExecutions<
  TData = Awaited<ReturnType<typeof cancelExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCancelExecutionRequest: BodyType<TLCancelExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCancelExecutionsQueryOptions(tLCancelExecutionRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Cancel Executions
 */
export const prefetchCancelExecutionsQuery = async <
  TData = Awaited<ReturnType<typeof cancelExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLCancelExecutionRequest: BodyType<TLCancelExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCancelExecutionsQueryOptions(tLCancelExecutionRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Cancel Executions
 */
export const invalidateCancelExecutions = async (
  queryClient: QueryClient,
  tLCancelExecutionRequest: BodyType<TLCancelExecutionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCancelExecutionsQueryKey(tLCancelExecutionRequest) }, options);

  return queryClient;
};

/**
 * @summary Delete Executions
 */
export const deleteExecutions = (
  tLDeleteExecutionRequest: BodyType<TLDeleteExecutionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDeleteExecutionResponse>(
    {
      url: `/v2/data-harmonization/executions/delete`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDeleteExecutionRequest,
      signal,
    },
    options,
  );
};

export const getDeleteExecutionsQueryKey = (tLDeleteExecutionRequest?: BodyType<TLDeleteExecutionRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'executions', 'delete', tLDeleteExecutionRequest] as const;
};

export const getDeleteExecutionsQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteExecutionRequest: BodyType<TLDeleteExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteExecutionsQueryKey(tLDeleteExecutionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteExecutions>>> = ({ signal }) =>
    deleteExecutions(tLDeleteExecutionRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteExecutions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteExecutionsQueryResult = NonNullable<Awaited<ReturnType<typeof deleteExecutions>>>;
export type DeleteExecutionsQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteExecutions<
  TData = Awaited<ReturnType<typeof deleteExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteExecutionRequest: BodyType<TLDeleteExecutionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteExecutions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteExecutions>>,
          TError,
          Awaited<ReturnType<typeof deleteExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteExecutions<
  TData = Awaited<ReturnType<typeof deleteExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteExecutionRequest: BodyType<TLDeleteExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteExecutions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteExecutions>>,
          TError,
          Awaited<ReturnType<typeof deleteExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteExecutions<
  TData = Awaited<ReturnType<typeof deleteExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteExecutionRequest: BodyType<TLDeleteExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Executions
 */

export function useDeleteExecutions<
  TData = Awaited<ReturnType<typeof deleteExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDeleteExecutionRequest: BodyType<TLDeleteExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteExecutionsQueryOptions(tLDeleteExecutionRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Executions
 */
export const prefetchDeleteExecutionsQuery = async <
  TData = Awaited<ReturnType<typeof deleteExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLDeleteExecutionRequest: BodyType<TLDeleteExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteExecutionsQueryOptions(tLDeleteExecutionRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Executions
 */
export const invalidateDeleteExecutions = async (
  queryClient: QueryClient,
  tLDeleteExecutionRequest: BodyType<TLDeleteExecutionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteExecutionsQueryKey(tLDeleteExecutionRequest) }, options);

  return queryClient;
};

/**
 * @summary Bulk Export Execution Results
 */
export const bulkExportExecutionResults = (
  tLBulkExportExecutionResultRequest: BodyType<TLBulkExportExecutionResultRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBulkExportExecutionResultResponse>(
    {
      url: `/v2/data-harmonization/executions/result/bulk-export`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLBulkExportExecutionResultRequest,
      signal,
    },
    options,
  );
};

export const getBulkExportExecutionResultsQueryKey = (
  tLBulkExportExecutionResultRequest?: BodyType<TLBulkExportExecutionResultRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'executions',
    'result',
    'bulk-export',
    tLBulkExportExecutionResultRequest,
  ] as const;
};

export const getBulkExportExecutionResultsQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkExportExecutionResults>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkExportExecutionResultRequest: BodyType<TLBulkExportExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkExportExecutionResults>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkExportExecutionResultsQueryKey(tLBulkExportExecutionResultRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkExportExecutionResults>>> = ({ signal }) =>
    bulkExportExecutionResults(tLBulkExportExecutionResultRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkExportExecutionResults>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkExportExecutionResultsQueryResult = NonNullable<Awaited<ReturnType<typeof bulkExportExecutionResults>>>;
export type BulkExportExecutionResultsQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkExportExecutionResults<
  TData = Awaited<ReturnType<typeof bulkExportExecutionResults>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkExportExecutionResultRequest: BodyType<TLBulkExportExecutionResultRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkExportExecutionResults>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkExportExecutionResults>>,
          TError,
          Awaited<ReturnType<typeof bulkExportExecutionResults>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkExportExecutionResults<
  TData = Awaited<ReturnType<typeof bulkExportExecutionResults>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkExportExecutionResultRequest: BodyType<TLBulkExportExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkExportExecutionResults>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkExportExecutionResults>>,
          TError,
          Awaited<ReturnType<typeof bulkExportExecutionResults>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkExportExecutionResults<
  TData = Awaited<ReturnType<typeof bulkExportExecutionResults>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkExportExecutionResultRequest: BodyType<TLBulkExportExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkExportExecutionResults>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Export Execution Results
 */

export function useBulkExportExecutionResults<
  TData = Awaited<ReturnType<typeof bulkExportExecutionResults>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkExportExecutionResultRequest: BodyType<TLBulkExportExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkExportExecutionResults>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkExportExecutionResultsQueryOptions(tLBulkExportExecutionResultRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Export Execution Results
 */
export const prefetchBulkExportExecutionResultsQuery = async <
  TData = Awaited<ReturnType<typeof bulkExportExecutionResults>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLBulkExportExecutionResultRequest: BodyType<TLBulkExportExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkExportExecutionResults>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkExportExecutionResultsQueryOptions(tLBulkExportExecutionResultRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Export Execution Results
 */
export const invalidateBulkExportExecutionResults = async (
  queryClient: QueryClient,
  tLBulkExportExecutionResultRequest: BodyType<TLBulkExportExecutionResultRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkExportExecutionResultsQueryKey(tLBulkExportExecutionResultRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Execution Result
 */
export const getExecutionResult = (
  { executionId }: GetExecutionResultPathParameters,
  tLGetExecutionResultRequest: BodyType<TLGetExecutionResultRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGetExecutionResultResponse>(
    {
      url: `/v2/data-harmonization/executions/${executionId}/result`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLGetExecutionResultRequest,
      signal,
    },
    options,
  );
};

export const getGetExecutionResultQueryKey = (
  { executionId }: GetExecutionResultPathParameters,
  tLGetExecutionResultRequest?: BodyType<TLGetExecutionResultRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'executions',
    executionId,
    'result',
    tLGetExecutionResultRequest,
  ] as const;
};

export const getGetExecutionResultQueryOptions = <
  TData = Awaited<ReturnType<typeof getExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GetExecutionResultPathParameters,
  tLGetExecutionResultRequest: BodyType<TLGetExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetExecutionResultQueryKey({ executionId }, tLGetExecutionResultRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getExecutionResult>>> = ({ signal }) =>
    getExecutionResult({ executionId }, tLGetExecutionResultRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getExecutionResult>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetExecutionResultQueryResult = NonNullable<Awaited<ReturnType<typeof getExecutionResult>>>;
export type GetExecutionResultQueryError = ErrorType<TLHTTPValidationError>;

export function useGetExecutionResult<
  TData = Awaited<ReturnType<typeof getExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetExecutionResultPathParameters,
  tLGetExecutionResultRequest: BodyType<TLGetExecutionResultRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionResult>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getExecutionResult>>,
          TError,
          Awaited<ReturnType<typeof getExecutionResult>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetExecutionResult<
  TData = Awaited<ReturnType<typeof getExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetExecutionResultPathParameters,
  tLGetExecutionResultRequest: BodyType<TLGetExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionResult>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getExecutionResult>>,
          TError,
          Awaited<ReturnType<typeof getExecutionResult>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetExecutionResult<
  TData = Awaited<ReturnType<typeof getExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetExecutionResultPathParameters,
  tLGetExecutionResultRequest: BodyType<TLGetExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Execution Result
 */

export function useGetExecutionResult<
  TData = Awaited<ReturnType<typeof getExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GetExecutionResultPathParameters,
  tLGetExecutionResultRequest: BodyType<TLGetExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetExecutionResultQueryOptions({ executionId }, tLGetExecutionResultRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Execution Result
 */
export const prefetchGetExecutionResultQuery = async <
  TData = Awaited<ReturnType<typeof getExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { executionId }: GetExecutionResultPathParameters,
  tLGetExecutionResultRequest: BodyType<TLGetExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetExecutionResultQueryOptions({ executionId }, tLGetExecutionResultRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Execution Result
 */
export const invalidateGetExecutionResult = async (
  queryClient: QueryClient,
  { executionId }: GetExecutionResultPathParameters,
  tLGetExecutionResultRequest: BodyType<TLGetExecutionResultRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetExecutionResultQueryKey({ executionId }, tLGetExecutionResultRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Export Execution Result
 */
export const exportExecutionResult = (
  { executionId }: ExportExecutionResultPathParameters,
  tLExportExecutionResultRequest: BodyType<TLExportExecutionResultRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLExportExecutionResultResponse>(
    {
      url: `/v2/data-harmonization/executions/${executionId}/result/export`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLExportExecutionResultRequest,
      signal,
    },
    options,
  );
};

export const getExportExecutionResultQueryKey = (
  { executionId }: ExportExecutionResultPathParameters,
  tLExportExecutionResultRequest?: BodyType<TLExportExecutionResultRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'executions',
    executionId,
    'result',
    'export',
    tLExportExecutionResultRequest,
  ] as const;
};

export const getExportExecutionResultQueryOptions = <
  TData = Awaited<ReturnType<typeof exportExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: ExportExecutionResultPathParameters,
  tLExportExecutionResultRequest: BodyType<TLExportExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getExportExecutionResultQueryKey({ executionId }, tLExportExecutionResultRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof exportExecutionResult>>> = ({ signal }) =>
    exportExecutionResult({ executionId }, tLExportExecutionResultRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof exportExecutionResult>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExportExecutionResultQueryResult = NonNullable<Awaited<ReturnType<typeof exportExecutionResult>>>;
export type ExportExecutionResultQueryError = ErrorType<TLHTTPValidationError>;

export function useExportExecutionResult<
  TData = Awaited<ReturnType<typeof exportExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportExecutionResultPathParameters,
  tLExportExecutionResultRequest: BodyType<TLExportExecutionResultRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportExecutionResult>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportExecutionResult>>,
          TError,
          Awaited<ReturnType<typeof exportExecutionResult>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportExecutionResult<
  TData = Awaited<ReturnType<typeof exportExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportExecutionResultPathParameters,
  tLExportExecutionResultRequest: BodyType<TLExportExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportExecutionResult>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportExecutionResult>>,
          TError,
          Awaited<ReturnType<typeof exportExecutionResult>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportExecutionResult<
  TData = Awaited<ReturnType<typeof exportExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportExecutionResultPathParameters,
  tLExportExecutionResultRequest: BodyType<TLExportExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Export Execution Result
 */

export function useExportExecutionResult<
  TData = Awaited<ReturnType<typeof exportExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: ExportExecutionResultPathParameters,
  tLExportExecutionResultRequest: BodyType<TLExportExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExportExecutionResultQueryOptions({ executionId }, tLExportExecutionResultRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Export Execution Result
 */
export const prefetchExportExecutionResultQuery = async <
  TData = Awaited<ReturnType<typeof exportExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { executionId }: ExportExecutionResultPathParameters,
  tLExportExecutionResultRequest: BodyType<TLExportExecutionResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExportExecutionResultQueryOptions({ executionId }, tLExportExecutionResultRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Export Execution Result
 */
export const invalidateExportExecutionResult = async (
  queryClient: QueryClient,
  { executionId }: ExportExecutionResultPathParameters,
  tLExportExecutionResultRequest: BodyType<TLExportExecutionResultRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getExportExecutionResultQueryKey({ executionId }, tLExportExecutionResultRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Generate Filters For Execution Result
 */
export const generateFiltersForExecutionResult = (
  { executionId }: GenerateFiltersForExecutionResultPathParameters,
  tLGenerateFiltersForExecutionRequest: BodyType<TLGenerateFiltersForExecutionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGenerateFiltersResponse>(
    {
      url: `/v2/data-harmonization/executions/${executionId}/result/generate-filters`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLGenerateFiltersForExecutionRequest,
      signal,
    },
    options,
  );
};

export const getGenerateFiltersForExecutionResultQueryKey = (
  { executionId }: GenerateFiltersForExecutionResultPathParameters,
  tLGenerateFiltersForExecutionRequest?: BodyType<TLGenerateFiltersForExecutionRequest>,
) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'executions',
    executionId,
    'result',
    'generate-filters',
    tLGenerateFiltersForExecutionRequest,
  ] as const;
};

export const getGenerateFiltersForExecutionResultQueryOptions = <
  TData = Awaited<ReturnType<typeof generateFiltersForExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GenerateFiltersForExecutionResultPathParameters,
  tLGenerateFiltersForExecutionRequest: BodyType<TLGenerateFiltersForExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGenerateFiltersForExecutionResultQueryKey({ executionId }, tLGenerateFiltersForExecutionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof generateFiltersForExecutionResult>>> = ({ signal }) =>
    generateFiltersForExecutionResult({ executionId }, tLGenerateFiltersForExecutionRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForExecutionResult>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GenerateFiltersForExecutionResultQueryResult = NonNullable<
  Awaited<ReturnType<typeof generateFiltersForExecutionResult>>
>;
export type GenerateFiltersForExecutionResultQueryError = ErrorType<TLHTTPValidationError>;

export function useGenerateFiltersForExecutionResult<
  TData = Awaited<ReturnType<typeof generateFiltersForExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GenerateFiltersForExecutionResultPathParameters,
  tLGenerateFiltersForExecutionRequest: BodyType<TLGenerateFiltersForExecutionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForExecutionResult>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof generateFiltersForExecutionResult>>,
          TError,
          Awaited<ReturnType<typeof generateFiltersForExecutionResult>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGenerateFiltersForExecutionResult<
  TData = Awaited<ReturnType<typeof generateFiltersForExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GenerateFiltersForExecutionResultPathParameters,
  tLGenerateFiltersForExecutionRequest: BodyType<TLGenerateFiltersForExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForExecutionResult>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof generateFiltersForExecutionResult>>,
          TError,
          Awaited<ReturnType<typeof generateFiltersForExecutionResult>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGenerateFiltersForExecutionResult<
  TData = Awaited<ReturnType<typeof generateFiltersForExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GenerateFiltersForExecutionResultPathParameters,
  tLGenerateFiltersForExecutionRequest: BodyType<TLGenerateFiltersForExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Generate Filters For Execution Result
 */

export function useGenerateFiltersForExecutionResult<
  TData = Awaited<ReturnType<typeof generateFiltersForExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { executionId }: GenerateFiltersForExecutionResultPathParameters,
  tLGenerateFiltersForExecutionRequest: BodyType<TLGenerateFiltersForExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGenerateFiltersForExecutionResultQueryOptions(
    { executionId },
    tLGenerateFiltersForExecutionRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Generate Filters For Execution Result
 */
export const prefetchGenerateFiltersForExecutionResultQuery = async <
  TData = Awaited<ReturnType<typeof generateFiltersForExecutionResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { executionId }: GenerateFiltersForExecutionResultPathParameters,
  tLGenerateFiltersForExecutionRequest: BodyType<TLGenerateFiltersForExecutionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof generateFiltersForExecutionResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGenerateFiltersForExecutionResultQueryOptions(
    { executionId },
    tLGenerateFiltersForExecutionRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Generate Filters For Execution Result
 */
export const invalidateGenerateFiltersForExecutionResult = async (
  queryClient: QueryClient,
  { executionId }: GenerateFiltersForExecutionResultPathParameters,
  tLGenerateFiltersForExecutionRequest: BodyType<TLGenerateFiltersForExecutionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGenerateFiltersForExecutionResultQueryKey({ executionId }, tLGenerateFiltersForExecutionRequest) },
    options,
  );

  return queryClient;
};
